import { ReactNode, ComponentType } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles, X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface FeatureDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: ComponentType<{ className?: string }>;
  title: string;
  shortDescription: string;
  longDescription: string;
  bullets: string[];
  screenshotSrc?: string;
  screenshotAlt?: string;
  /**
   * Custom hero content to render INSIDE the gradient hero band when no real
   * screenshot is available yet. Takes precedence over the icon-only fallback
   * but is overridden by `screenshotSrc` so swapping in a real screenshot
   * later is a one-line change at the call site.
   */
  customHero?: ReactNode;
  ctaLabel?: string;
  ctaHref?: string;
  /** Tailwind gradient stops, e.g. "from-primary/20 to-accent/20" */
  gradient?: string;
  bulletsTitle?: string;
}

/**
 * Premium dialog showing a feature in depth: hero screenshot (or stylised
 * placeholder), long description, "what you can do" bullets and a CTA.
 *
 * Centering strategy:
 *  - The outer Radix Content is the positioned/centered shell. Its transform
 *    (`translate(-50%, -50%)`) is NEVER changed during open/close.
 *  - Animation lives on an inner wrapper and is opacity-only, so the popup
 *    cannot drift, slide, or zoom relative to the viewport center.
 *
 * Dismiss policy (consistent for every landing card popup):
 *  - Close on the X button, click outside (overlay), or Escape.
 *  - Mouse movement inside or near the popup never closes it.
 */
export function FeatureDetailDialog({
  open,
  onOpenChange,
  icon: Icon,
  title,
  shortDescription,
  longDescription,
  bullets,
  screenshotSrc,
  screenshotAlt,
  customHero,
  ctaLabel,
  ctaHref,
  gradient = "from-primary/25 to-accent/20",
  bulletsTitle,
}: FeatureDetailDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/80",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          )}
        />
        {/* Outer shell: locked-center, NO transform animations */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-2xl",
            "-translate-x-1/2 -translate-y-1/2",
            "outline-none",
          )}
        >
          {/* Inner wrapper: opacity-only fade so centering is never overridden */}
          <div
            className={cn(
              "card-premium border border-primary/20 rounded-2xl overflow-hidden shadow-glass-hover",
              "transition-opacity duration-200",
              open ? "opacity-100" : "opacity-0",
            )}
          >
            {/* Hero / screenshot */}
            <div
              className={cn(
                "relative h-44 sm:h-56 w-full overflow-hidden bg-gradient-to-br",
                gradient,
              )}
            >
              {screenshotSrc ? (
                <img
                  src={screenshotSrc}
                  alt={screenshotAlt || title}
                  className="absolute inset-0 w-full h-full object-cover object-top opacity-90"
                  loading="lazy"
                />
              ) : customHero ? (
                customHero
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-3xl bg-card/40 backdrop-blur-xl border border-primary/30 flex items-center justify-center shadow-glass">
                    <Icon className="w-12 h-12 text-primary" />
                  </div>
                </div>
              )}
              {/* gradient fade for legibility */}
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent pointer-events-none" />

              {/* Close button */}
              <DialogPrimitive.Close
                className="absolute right-3 top-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-background/70 backdrop-blur-md border border-white/15 text-foreground/80 hover:text-foreground hover:bg-background/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="p-6 sm:p-8 space-y-5 max-h-[70vh] overflow-y-auto">
              <DialogHeader className="space-y-2 text-left">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="text-[0.65rem] font-medium tracking-[0.18em] uppercase text-primary/80">
                    {shortDescription}
                  </span>
                </div>
                <DialogTitle className="font-serif italic text-2xl sm:text-3xl leading-tight">
                  <span className="bg-gradient-primary bg-clip-text text-transparent pb-1 inline-block">
                    {title}
                  </span>
                </DialogTitle>
                <DialogDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {longDescription}
                </DialogDescription>
              </DialogHeader>

              {bullets.length > 0 && (
                <div className="space-y-2.5">
                  {bulletsTitle && (
                    <h4 className="text-xs font-semibold tracking-wider uppercase text-foreground/80">
                      {bulletsTitle}
                    </h4>
                  )}
                  <ul className="space-y-2">
                    {bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/90">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
                          <Check className="w-3 h-3 text-primary" />
                        </span>
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {ctaLabel && ctaHref && (
                <div className="pt-2">
                  <Button asChild variant="hero" size="lg" className="w-full sm:w-auto group">
                    <Link to={ctaHref} onClick={() => onOpenChange(false)}>
                      {ctaLabel}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export default FeatureDetailDialog;
